import React, { useEffect, useState } from "react";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import PrintButton from "@components/FlowActionButtons/PrintButton";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE, GENERAL_INFO_EDITABLE_STATUSES } from "../constants";
import { useFinalLoadingReport } from "../hooks/useFinalLoadingReport";
import { useGatedOpen } from "../hooks/usePollingGate";
import { problemMessage } from "../utils/problemDetail";
import GeneralInfoDrawer from "./GeneralInfoDrawer";
import {
  CombinationsSection,
  ContactsSection,
  GeneralInfoSection,
  ShipLoadingSection,
  ShipperSplitSection,
  StowageSection,
} from "./LoadingReportSections";
import { usePrintReport } from "./usePrintReport";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.final-report`;

/**
 * DBCO-0059 (R-31) - the Final Loading Report as the API projects it (nothing is computed here): the six sections, Modify for the
 * two Operations-entered fields and Print. A failed read shows a toast and a one-line fallback instead of an empty page.
 */
const FinalLoadingReportView: React.FC<{
  operationId: string;
  status: string | null | undefined;
}> = ({ operationId, status }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const report = useFinalLoadingReport(operationId);
  const print = usePrintReport("loading");
  const [modifying, setModifying] = useState(false);
  useGatedOpen(modifying);

  useEffect(() => {
    if (report.isError) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(report.error) ?? t(`${L}.load-failed`),
      });
    }
  }, [report.isError, report.errorUpdatedAt, report.error, notify, t]);

  const canModify = GENERAL_INFO_EDITABLE_STATUSES.includes(status ?? "");

  return (
    <Box
      data-testid="final-loading-report"
      sx={{ display: "flex", flexDirection: "column", gap: 3, position: "relative", minHeight: 80 }}
    >
      <LoadingOverlay loading={report.isLoading} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {canModify && report.data && (
          <Button
            variant="outlined"
            testId="final-report-modify"
            startIcon={<SyncIcon />}
            onClick={() => setModifying(true)}
            label={<span className="normal-case font-medium">{t(`${L}.modify-button`)}</span>}
          />
        )}
        <PrintButton
          langBase={L}
          permission=""
          isActionAvailable
          onPrint={() => void print(operationId)}
        />
      </Box>
      {report.isError && (
        <Typography data-testid="final-report-unavailable" color="text.secondary">
          {t(`${L}.not-available`)}
        </Typography>
      )}
      {report.data && (
        <>
          <GeneralInfoSection report={report.data} />
          <CombinationsSection report={report.data} />
          <ShipLoadingSection report={report.data} />
          <ShipperSplitSection report={report.data} />
          <StowageSection report={report.data} />
          <ContactsSection report={report.data} />
        </>
      )}
      {modifying && report.data && (
        <GeneralInfoDrawer
          operationId={operationId}
          info={report.data.generalInfo}
          onClose={() => setModifying(false)}
        />
      )}
    </Box>
  );
};

export default FinalLoadingReportView;
